import * as React from 'react';

import {
  Box, Chip, Collapse, Divider, Grid, IconButton, InputLabel, List, ListItem, ListItemButton, ListItemText, Typography,
} from '@mui/material';

import { TransitionGroup } from 'react-transition-group';
import DeleteIcon from '@mui/icons-material/Delete';
import { Link } from 'react-router-dom';
import ConfirmationDialog from './ConfirmationDialog';

import * as clients from '../core/api/clients';
import { IReportMetadata } from '../core/api/clients';

interface IReportMetadataList {
  reports: Array<IReportMetadata>;
  handleDeleteRequest: (id: number) => void;
  uriSlug: 'flight-event' | 'cabin-event' | 'dispatch-event' | 'flight-operation-notification';
}

function ReportMetadataList({ reports, handleDeleteRequest, uriSlug }: IReportMetadataList) {
  const [confDialogOpen, setConfDialogOpen] = React.useState<boolean>(false);
  const [deleteId, setDeleteId] = React.useState<number | undefined>(undefined);
  const [hoveredId, setHoveredId] = React.useState<number | undefined>(-1);
  const getStatusColor = (status: clients.EventFormStatus | undefined) => {
    switch (status) {
      case clients.EventFormStatus.Draft: return 'info';
      case clients.EventFormStatus.Submitted: return 'warning';
      case clients.EventFormStatus.Approved: return 'success';
      case clients.EventFormStatus.Final: return 'primary';
      case clients.EventFormStatus.Deleted: return 'error';
      default: return undefined;
    }
  };
  const openDeleteConfirmationDialog = (id: number | undefined): void => {
    setConfDialogOpen(true);
    setDeleteId(id);
  };

  const handleConfirmDelete = () => {
    if (deleteId !== undefined) {
      handleDeleteRequest(deleteId);
      setDeleteId(undefined);
    }
  };
  const showDeletedSubmission = (id: number | undefined) => {
    setHoveredId(id);
  };
  const hideDeletedSubmission = () => {
    setHoveredId(-1);
  };
  return (
    <List disablePadding>
      <ConfirmationDialog isOpen={confDialogOpen} setOpen={setConfDialogOpen} confirm={handleConfirmDelete} />
      {reports.length <= 0
        ? (
          <Box paddingY={5}>
            <Divider variant="fullWidth" />
            <Box paddingY={3} display="flex" justifyContent="center">
              <Typography fontSize={15}>No rows</Typography>
            </Box>
            <Divider variant="fullWidth" />
          </Box>
        )
        : (
          <TransitionGroup>
            {reports.map((report) => (
              <Collapse key={report.id}>
                <ListItem
                  component="div"
                  disablePadding
                  secondaryAction={(
                    <Box display="flex" sx={{ pr: 2, minWidth: 40 }}>
                      {report.status === clients.EventFormStatus.Draft ? (
                        <IconButton onClick={() => openDeleteConfirmationDialog(report.id)}>
                          <DeleteIcon />
                        </IconButton>
                      ) : null}
                    </Box>
              )}
                >
                  <ListItemButton
                    component={Link}
                    to={`/form/${uriSlug}/${report.id}/${report.status === clients.EventFormStatus.Draft ? 'edit' : 'view'}`}
                    sx={{ padding: 2, flexGrow: 1, width: '100%' }}
                    onMouseEnter={() => showDeletedSubmission(report.id)}
                    onMouseLeave={() => hideDeletedSubmission()}
                  >
                    <Grid container spacing={2} sx={{ width: '100%' }}>
                      <Grid item xs={8} md={10}>
                        <ListItemText primary={report.title} secondary={report.subtitle} />
                      </Grid>
                      <Grid item xs={4} md={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Chip variant="outlined" label={report.status} color={getStatusColor(report.status)} />
                      </Grid>

                      {
                  (report.status === clients.EventFormStatus.Deleted) && (report.id === hoveredId)
                  && (
                    <Grid container item md={5} spacing={3}>
                      <Grid item>
                        <InputLabel>Reason for Deletion:</InputLabel>
                      </Grid>
                      <Grid item>
                        <InputLabel>
                          {report.reasonDeleted}
                        </InputLabel>
                      </Grid>
                    </Grid>
                  )
                  }
                    </Grid>
                  </ListItemButton>
                </ListItem>

              </Collapse>
            ))}
          </TransitionGroup>
        )}
    </List>
  );
}

export default ReportMetadataList;
